/**
 * 追问对话页
 *
 * /chat —— 我的会话列表
 * /chat/:id —— 进入某次会话
 *
 * 左侧咨询档案，右侧顾问室消息流（流式 SSE）+ 建议问题 + 配额提示
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  chatApi,
  quotaApi,
  type ChatMessage,
  type ChatSessionDetail,
  type ChatSessionSummary,
  type QuotaStatus,
} from '@/lib/stage3.api';
import { useToast } from '@/components/ui/Toast';
import { AdvisorShell, SuggestionChips } from '@/components/premium/Experience';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { SelectField } from '@/components/ui/PremiumControls';
import { AnalyzingOverlay } from '@/components/premium/AnalyzingOverlay';
import { listMyCharts, type ChartHistoryItem } from '@/lib/chart.api';

function immersiveShellClass(historyCollapsed: boolean) {
  return `mobile-immersive-shell relative mx-auto grid h-full min-h-0 w-full max-w-[1680px] overflow-hidden rounded-2xl border border-border bg-bg-1 shadow-gold lg:rounded-3xl ${
    historyCollapsed
      ? 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[0_minmax(0,1fr)]'
      : 'grid-cols-[minmax(0,1fr)] lg:grid-cols-[360px_minmax(0,1fr)]'
  }`;
}

function immersiveHistoryClass(historyCollapsed: boolean) {
  return `mobile-immersive-history absolute inset-y-0 left-0 z-30 h-full w-[min(82vw,360px)] min-h-0 overflow-y-auto border-r border-border bg-bg p-4 shadow-gold transition-transform duration-base lg:relative lg:z-auto lg:w-auto lg:shadow-none lg:p-8 ${
    historyCollapsed
      ? '-translate-x-full lg:invisible lg:p-0 lg:opacity-0'
      : 'translate-x-0 lg:visible lg:opacity-100'
  }`;
}

function HistoryToggle({ collapsed, onClick }: { collapsed: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-3 top-3 z-40 inline-flex items-center gap-2 rounded-full border border-border-accent bg-accent px-4 py-2 text-sm font-medium text-accent-fg shadow-gold transition-all hover:bg-accent-hover active:bg-accent-active lg:left-4 lg:top-4"
      aria-label={collapsed ? '查看历史记录' : '隐藏历史记录'}
    >
      <span aria-hidden="true">☰</span>
      <span>{collapsed ? '查看历史' : '隐藏历史'}</span>
    </button>
  );
}

function formatChartOptionDescription(chart: ChartHistoryItem) {
  const gender = chart.gender === 'male' ? '男' : chart.gender === 'female' ? '女' : '未指定';
  return `${gender} · ${chart.baziKey}`;
}

export function ChatPage() {
  const { id } = useParams();
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [charts, setCharts] = useState<ChartHistoryItem[]>([]);
  const [selectedChartId, setSelectedChartId] = useState('');
  const [loadingCharts, setLoadingCharts] = useState(true);
  const [historyCollapsed, setHistoryCollapsed] = useState(() =>
    typeof window === 'undefined' ? false : window.innerWidth < 1024,
  );

  const shellClass = useMemo(() => immersiveShellClass(historyCollapsed), [historyCollapsed]);
  const historyClass = useMemo(() => immersiveHistoryClass(historyCollapsed), [historyCollapsed]);

  useEffect(() => {
    void chatApi
      .list()
      .then(setSessions)
      .catch(() => setSessions([]));
    void listMyCharts()
      .then((list) => {
        setCharts(list);
        if (list.length > 0) setSelectedChartId(list[0].id);
      })
      .catch(() => setCharts([]))
      .finally(() => setLoadingCharts(false));
  }, []);

  if (loadingCharts) {
    return (
      <main className="flex-1 flex items-center justify-center px-6 text-sm text-fg-muted">
        正在读取命盘…
      </main>
    );
  }

  if (charts.length === 0) {
    return (
      <NoChartHint
        title="观微先生 · 追问对话"
        description="追问必须绑定一张命盘，这样大模型才能结合你的八字结构回答。"
      />
    );
  }

  return (
    <main className="mobile-immersive-page h-full min-h-0 overflow-hidden px-3 py-3 sm:px-6 lg:px-8 lg:py-6">
      <div className={shellClass}>
        <aside className={historyClass}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-lg text-accent">咨询档案</h2>
            <NewSessionButton
              chartId={selectedChartId}
              onCreated={(s) => setSessions((prev) => [s, ...prev])}
            />
          </div>
          <div className="mb-4">
            <SelectField
              label="绑定命盘"
              value={selectedChartId}
              onChange={setSelectedChartId}
              placeholder="选择命盘"
              options={charts.map((chart) => ({
                value: chart.id,
                label: chart.displayName,
                description: formatChartOptionDescription(chart),
              }))}
            />
            <p className="mt-2 text-xs leading-relaxed text-fg-muted">
              新建追问会话会固定使用这张命盘。
            </p>
          </div>
          <ul className="space-y-1">
            {sessions.map((s) => (
              <li key={s.id}>
                <Link
                  to={`/chat/${s.id}`}
                  className={
                    'block px-3 py-2 rounded text-sm transition-colors duration-fast ' +
                    (id === s.id
                      ? 'bg-accent-soft text-accent'
                      : 'text-fg-1 hover:bg-bg-2 hover:text-fg')
                  }
                >
                  <p className="truncate">{s.title || '追问会话'}</p>
                  <p className="text-xs text-fg-muted">
                    {new Date(s.updatedAt).toLocaleString()} ·{' '}
                    {s.lastMessageStatus === 'streaming' ? '生成中' : '已完成'}
                  </p>
                </Link>
              </li>
            ))}
            {sessions.length === 0 && <li className="text-sm text-fg-muted">暂无会话</li>}
          </ul>
        </aside>
        <section className="mobile-immersive-content relative order-2 h-full min-h-0 overflow-y-auto overscroll-contain bg-bg-1 p-4 sm:p-8 lg:p-10">
          <HistoryToggle
            collapsed={historyCollapsed}
            onClick={() => setHistoryCollapsed((value) => !value)}
          />
          {id ? <SessionPanel sessionId={id} /> : <EmptyHint />}
        </section>
      </div>
    </main>
  );
}

function NewSessionButton({
  chartId,
  onCreated,
}: {
  chartId: string;
  onCreated: (s: ChatSessionSummary) => void;
}) {
  const navigate = useNavigate();
  const showToast = useToast();
  return (
    <button
      onClick={async () => {
        if (!chartId) {
          showToast('请先选择命盘', 'error');
          return;
        }
        try {
          const s = await chatApi.createSession({ title: '新的追问', chartId });
          onCreated(s);
          navigate(`/chat/${s.id}`);
        } catch (e) {
          showToast((e as Error).message, 'error');
        }
      }}
      className="text-xs text-accent hover:underline"
    >
      + 新建
    </button>
  );
}

function NoChartHint({ title, description }: { title: string; description: string }) {
  return (
    <main className="flex-1 flex items-center justify-center px-6">
      <Card className="max-w-md p-10 text-center">
        <p className="mb-3 font-serif text-2xl text-fg">{title}</p>
        <p className="mb-6 text-sm leading-7 text-fg-2">{description}</p>
        <Link to="/chart">
          <Button size="lg">先去排盘</Button>
        </Link>
      </Card>
    </main>
  );
}

function EmptyHint() {
  return (
    <div className="h-full flex items-center justify-center text-fg-muted text-sm border border-dashed border-border rounded-lg p-12">
      <div className="text-center">
        <p className="font-serif text-2xl text-accent mb-2">观微先生 · 追问对话</p>
        <p>选择左侧咨询档案，或点「+ 新建」开始一段新的对话</p>
      </div>
    </div>
  );
}

function SessionPanel({ sessionId }: { sessionId: string }) {
  const showToast = useToast();
  const [detail, setDetail] = useState<ChatSessionDetail | null>(null);
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState('');
  const [quota, setQuota] = useState<QuotaStatus | null>(null);
  const [paywall, setPaywall] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const reload = async () => {
    const d = await chatApi.detail(sessionId);
    setDetail(d);
  };

  useEffect(() => {
    void reload();
    void quotaApi.query(['chat.daily']).then((q) => setQuota(q['chat.daily']));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages.length, streamBuffer]);

  const onSend = () => {
    if (!draft.trim() || streaming) return;
    const text = draft.trim();
    setDraft('');
    setStreamBuffer('');
    setStreaming(true);
    setPaywall(null);
    const ac = new AbortController();
    abortRef.current = ac;

    // 乐观插入用户消息
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            messages: [
              ...prev.messages,
              {
                id: 'optimistic-' + Date.now(),
                role: 'user',
                content: text,
                createdAt: new Date().toISOString(),
              } as ChatMessage,
            ],
          }
        : prev,
    );

    void chatApi.askStream(
      sessionId,
      { userMessage: text },
      {
        onDelta: (d) => setStreamBuffer((b) => b + d),
        onDone: (meta) => {
          setStreaming(false);
          if (meta?.error === 'QUOTA_EXHAUSTED') {
            setPaywall('chat.quota_exhausted');
          } else if (meta?.error === 'BLOCKED') {
            const hint = typeof meta?.hint === 'string' ? meta.hint : '提问被合规规则拦截';
            showToast(hint, 'error');
          }
          if (meta?.quota) {
            setQuota(meta.quota as QuotaStatus);
          }
          // 重新拉取确保数据一致
          void reload();
          setStreamBuffer('');
        },
        onError: (e) => {
          setStreaming(false);
          showToast(e.message, 'error');
        },
      },
      ac.signal,
    );
  };

  const onCancel = () => {
    abortRef.current?.abort();
    setStreaming(false);
  };

  return (
    <AdvisorShell>
      <div className="flex h-full min-h-0 flex-col">
        {detail && !detail.chartId && (
          <div className="mx-5 mt-5 rounded-xl border border-border bg-bg-2 p-4 text-sm text-fg-1">
            <p className="font-serif text-accent mb-2">该会话未绑定命盘</p>
            <p className="mb-3 text-fg-muted">追问需要结合命盘信息，请新建一个已绑定命盘的会话。</p>
            <Link to="/chart" className="text-accent hover:underline">
              去排盘
            </Link>
          </div>
        )}
        {quota && !quota.unlimited && (
          <p className="px-5 pt-4 text-xs text-fg-muted">
            今日追问剩余：<span className="text-accent">{quota.remaining}</span> / 共{' '}
            {quota.used + quota.remaining} 次
          </p>
        )}

        <div className="flex-1 overflow-y-auto space-y-4 p-5">
          {detail?.messages.map((m) => (
            <MessageBubble key={m.id} m={m} />
          ))}
          {streaming && !streamBuffer && <AnalyzingOverlay />}
          {streaming && streamBuffer && (
            <MessageBubble
              m={{
                id: 'streaming',
                role: 'assistant',
                content: streamBuffer + '▍',
                createdAt: new Date().toISOString(),
              }}
            />
          )}
          <div ref={bottomRef} />
        </div>

        {paywall && (
          <div className="mx-5 my-3 rounded-xl border border-accent bg-accent-soft p-4 text-sm">
            <p className="font-serif text-accent mb-2">今日免费追问已用完</p>
            <p className="text-fg-1 mb-3">升级会员可获得更多追问额度，或单次解锁继续提问。</p>
            <Link
              to="/membership"
              className="inline-block px-4 py-2 rounded bg-accent text-bg text-sm"
            >
              前往升级
            </Link>
          </div>
        )}

        <div className="border-t border-border p-5 space-y-3">
          <SuggestionChips suggestions={CHAT_SUGGESTIONS} onPick={setDraft} />
          <div className="flex items-end gap-2">
            <textarea
              className="flex-1 resize-none rounded-xl border border-border bg-bg px-4 py-3 text-sm leading-7 text-fg focus:border-accent focus:outline-none"
              rows={2}
              placeholder="向观微先生提问..."
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                  e.preventDefault();
                  onSend();
                }
              }}
              disabled={streaming || Boolean(detail && !detail.chartId)}
            />
            {streaming ? (
              <Button variant="outline" onClick={onCancel}>
                停止
              </Button>
            ) : (
              <Button
                onClick={onSend}
                disabled={!draft.trim() || Boolean(detail && !detail.chartId)}
              >
                发送
              </Button>
            )}
          </div>
        </div>
      </div>
    </AdvisorShell>
  );
}

function MessageBubble({ m }: { m: ChatMessage }) {
  const mine = m.role === 'user';
  return (
    <div className={mine ? 'flex justify-end' : 'flex justify-start'}>
      <div
        className={
          'max-w-[92%] rounded-xl border px-4 py-3 text-sm leading-7 whitespace-pre-wrap sm:max-w-[80%] ' +
          (mine ? 'border-accent bg-accent-soft text-accent' : 'border-border bg-bg-2 text-fg')
        }
      >
        {m.content}
      </div>
    </div>
  );
}

const CHAT_SUGGESTIONS = [
  '我现在最该优先处理哪类事业问题？',
  '这段关系的核心冲突点是什么？',
  '未来三个月有什么需要避开的决策？',
  '我的行动清单可以更具体一点吗？',
];
